import { useState, useRef } from "react";

import { FormattedMessage, useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { LoadingButton } from "@atlaskit/button";
import { FormFooter } from "@atlaskit/form";
import Heading from "@atlaskit/heading";
import { Box, Flex, Inline, Stack, xcss } from "@atlaskit/primitives";
import {
    SubmitButton,
    SubmitButtonRef
} from "@atlassian/invite-people/dist/types/types";

import { useSearchParameters } from "@/components/signup/hooks";
import { PercievedProvisioningTiming } from "@/components/signup/provisioning-funnel-ufo";
import { UfoCustomTimingKey } from "@/components/signup/utils";
import { ufoEventOnboardingInviteUsers } from "@/components/signup/utils";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import { ANALYTICS_PREFIX } from "../../../../common/constants";
import { PageInviteLeftProps } from "../../../../common/types";
import { useWizardConfiguration } from "../../../../controllers";
import { onInviteUsersRedirectFailureInvite } from "../../../../utils/redirect-logging";
import { getEmailListAnalytics } from "../utils";

import { InvitePeopleWrapper } from "./invite-people-wrapper";
import messages from "./messages";
import { ShareLink } from "./share-link";

const FAILSAFE_REDIRECT_TIMEOUT = 20000;

export const LeftHandSidePageInvite = ({
    onSubmit,
    onClickNext,
    Header
}: PageInviteLeftProps) => {
    const { formatMessage } = useIntl();
    const [isInvitingUsers, setIsInvitingUsers] = useState(false);
    const [isSubmittingInvite, setIsSubmittingInvite] = useState(false);
    const [isSubmittingSkip, setIsSubmittingSkip] = useState(false);

    const [emailList, setEmailList] = useState<string[]>([]);
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const {
        teamType,
        projectDetails: { template }
    } = useWizardConfiguration();
    const { signupId } = useSearchParameters();

    const submitRef = useRef<HTMLButtonElement & SubmitButton>(null);

    const onInviteStart = () => {
        setIsInvitingUsers(true);
    };

    const onClickNextSubmit = async () => {
        setIsSubmittingInvite(true);
        ufoEventOnboardingInviteUsers.setCustomData({ signupId });

        const { emailCount, isCrossDomain } = getEmailListAnalytics(emailList);

        await Promise.resolve(
            createAnalyticsEvent({
                action: "clicked",
                actionSubject: "button",
                actionSubjectId: `${ANALYTICS_PREFIX}InviteSubmitButton`,
                eventType: "ui",
                attributes: {
                    emailCount,
                    isCrossDomain,
                    teamType,
                    template
                }
            }).fire()
        );

        if (emailList.length === 0) {
            if (onClickNext) {
                onClickNext();
            }

            return;
        }

        setTimeout(() => {
            onInviteUsersRedirectFailureInvite("time-out");
            if (onClickNext) {
                onClickNext();
            }
        }, FAILSAFE_REDIRECT_TIMEOUT);

        //@ts-ignore
        await submitRef?.current?.onClick();
    };

    const onClickNextDoThisLater = () => {
        setIsSubmittingSkip(true);

        createAnalyticsEvent({
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: `${ANALYTICS_PREFIX}InviteSkipButton`,
            eventType: "ui"
        }).fire();

        if (onClickNext) {
            onClickNext();
        }
    };

    return (
        <>
            <Flex
                direction="column"
                justifyContent="space-between"
                xcss={containerStyles}
            >
                {checkFeatureFlagGate(
                    featureFlags.provisioningFunnelUfoImprovements
                ) &&
                    isInvitingUsers && (
                        <PercievedProvisioningTiming
                            provisioningFunnelTimingKey={
                                UfoCustomTimingKey.PERCEIVED_PROVISIONING_FUNNEL_INVITE_TEAMMATES
                            }
                        />
                    )}
                <Stack
                    space={
                        checkFeatureFlagGate(
                            featureFlags.jiraOnboardingAiMadlibV2
                        ) && Header
                            ? "space.0"
                            : "space.400"
                    }
                >
                    {checkFeatureFlagGate(
                        featureFlags.jiraOnboardingAiMadlibV2
                    ) && Header ? (
                        <Header
                            header={
                                isInvitingUsers
                                    ? formatMessage(messages.titleSubmitting)
                                    : formatMessage(messages.title)
                            }
                            subheader={
                                isInvitingUsers
                                    ? formatMessage(
                                          messages.bodyLineOneSubmitting
                                      )
                                    : formatMessage(messages.bodyLineOne)
                            }
                        />
                    ) : (
                        <Stack space="space.150">
                            <Heading as="h1" size="xlarge">
                                <FormattedMessage
                                    {...(isInvitingUsers
                                        ? messages.titleSubmitting
                                        : messages.title)}
                                />
                            </Heading>
                            <Box xcss={subtextStyles}>
                                <FormattedMessage
                                    {...(isInvitingUsers
                                        ? messages.bodyLineOneSubmitting
                                        : messages.bodyLineOne)}
                                />
                            </Box>
                        </Stack>
                    )}
                    <Flex direction="column" alignItems="start">
                        <ShareLink />
                        <InvitePeopleWrapper
                            submitRef={submitRef as SubmitButtonRef}
                            onClickNext={onClickNext}
                            onInviteStart={onInviteStart}
                            setEmailList={setEmailList}
                            showEmailInput={!!onSubmit}
                        />
                    </Flex>
                </Stack>
            </Flex>
            <FormFooter>
                <Flex
                    justifyContent="end"
                    alignItems="center"
                    xcss={footerStyles}
                >
                    <Inline
                        alignBlock="center"
                        alignInline="end"
                        space="space.050"
                    >
                        <LoadingButton
                            appearance="subtle"
                            onClick={() => {
                                onClickNextDoThisLater();

                                if (onSubmit) {
                                    onSubmit();
                                }
                            }}
                            isDisabled={
                                emailList.length > 0 ||
                                isSubmittingInvite ||
                                isInvitingUsers
                            }
                            isLoading={isSubmittingSkip}
                        >
                            <FormattedMessage {...messages.doThisLater} />
                        </LoadingButton>
                        <LoadingButton
                            appearance="primary"
                            onClick={() => {
                                if (onSubmit) {
                                    onSubmit(emailList);
                                } else {
                                    onClickNextSubmit();
                                }
                            }}
                            isDisabled={isSubmittingSkip}
                            isLoading={isInvitingUsers || isSubmittingInvite}
                            ref={submitRef}
                        >
                            <FormattedMessage
                                {...(emailList.length > 0
                                    ? messages.inviteAndGoToJira
                                    : messages.goToJira)}
                            />
                        </LoadingButton>
                    </Inline>
                </Flex>
            </FormFooter>
        </>
    );
};

const containerStyles = xcss({
    height: "100%"
});

const subtextStyles = xcss({
    color: "color.text.subtle",
    minHeight: "size.500"
});

const footerStyles = xcss({
    width: "100%",
    color: "color.text.subtlest"
});
